import { AssayResult, Flags, NormalisationMode, type StrippedFullAbundance } from './types';

export const NormalisationModesAndProperties = {
  [NormalisationMode.MG_SS]: 'copiesPerMgSS',
  [NormalisationMode.HOUR]: 'copiesPerHour',
  [NormalisationMode.LITRE_16S]: 'copiesPerL',
  [NormalisationMode.TEN_UL_DILUTED_DNA_16S]: 'absolute',
  [NormalisationMode.LITRE_AY]: 'copiesPerL',
  [NormalisationMode.TEN_UL_DILUTED_DNA_AY]: 'absolute',
  [NormalisationMode.MG_BOD]: 'copiesPerMgBod',
  [NormalisationMode.SIXTEEN_S]: 'relative',
} as const; // satisfies Partial<Record<NormalisationMode, keyof FullAbundance>>;

export const DEFAULT_ASSAY_MODE_LOQ_CQ = 28;

export function isConsideredAbsolute(normalisationMode: NormalisationMode) {
  return normalisationMode !== NormalisationMode.SIXTEEN_S;
}

export function isAyNormalisationMode(normalisationMode: NormalisationMode) {
  return (
    normalisationMode === NormalisationMode.LITRE_AY || normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_AY
  );
}

export function getAyModeLoqCq(loqCq: number | null | undefined) {
  return loqCq ?? DEFAULT_ASSAY_MODE_LOQ_CQ;
}

export function getAyModeFlag(
  meanCt: number | null | undefined,
  baseFlag: Flags | null | undefined,
  loqCq: number | null | undefined,
  cleanlyNotDetectedWhenMeanCtMissing: boolean = false,
  treatHighCtAsUnquantified: boolean = false,
): Flags | null {
  if (baseFlag != null && baseFlag !== Flags.TRACES) {
    return baseFlag;
  }

  if (meanCt == null) {
    return cleanlyNotDetectedWhenMeanCtMissing ? null : Flags.UNCLEAR;
  }

  if (meanCt >= DEFAULT_ASSAY_MODE_LOQ_CQ) {
    return treatHighCtAsUnquantified ? Flags.UNQUANTIFIED : Flags.UNCLEAR;
  }

  if (meanCt >= getAyModeLoqCq(loqCq)) {
    return Flags.UNQUANTIFIED;
  }

  return null;
}

export function getFlagForNormalisationMode(
  abundance: {
    assay?: string;
    gene?: string;
    relative?: number | null;
    absolute?: number | null;
    copiesPerL?: number | null;
    copiesPerHour?: number | null;
    copiesPerMgSS?: number | null;
    copiesPerMgBod?: number | null;
    valuesByMode?: Record<string, number | null> | undefined;
    flag?: Flags | null;
    flagsByMode?: Record<string, Flags | null> | undefined;
  },
  normalisationMode: NormalisationMode,
) {
  const modeFlag = abundance.flagsByMode?.[normalisationMode];
  if (modeFlag !== undefined) {
    return modeFlag;
  }

  if (normalisationMode === NormalisationMode.SIXTEEN_S) {
    return abundance.flag ?? null;
  }

  return getNormalisedValue(abundance as StrippedFullAbundance, normalisationMode) != null ? (abundance.flag ?? null) : null;
}

export function hasNormalisationModeData(
  abundance: StrippedFullAbundance & {
    flag?: Flags | null;
    flagsByMode?: Record<string, Flags | null> | undefined;
    estimates?: Record<string, number | null> | undefined;
    estimateLows?: Record<string, number | null> | undefined;
    estimateHighs?: Record<string, number | null> | undefined;
    lowerBounds?: Record<string, number | null> | undefined;
    upperBounds?: Record<string, number | null> | undefined;
    techRepValuesByMode?: Record<string, Array<number | null>> | undefined;
  },
  normalisationMode: keyof typeof NormalisationModesAndProperties,
) {
  // Only treat a mode as present when the payload explicitly carries mode-specific
  // data or a legacy scalar value for that exact mode. This avoids leaking
  // relative/16S trace flags into unsupported volume-normalised views.
  if (
    Object.prototype.hasOwnProperty.call(abundance.valuesByMode ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.flagsByMode ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.estimates ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.estimateLows ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.estimateHighs ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.lowerBounds ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.upperBounds ?? {}, normalisationMode) ||
    Object.prototype.hasOwnProperty.call(abundance.techRepValuesByMode ?? {}, normalisationMode) ||
    getNormalisedValue(abundance, normalisationMode) != null
  ) {
    return true;
  }

  if (normalisationMode === NormalisationMode.SIXTEEN_S) {
    return abundance.flag != null;
  }

  return false;
}

export function isDetectedForNormalisationMode(
  abundance: StrippedFullAbundance & {
    flag?: Flags | null;
    flagsByMode?: Record<string, Flags | null> | undefined;
  },
  normalisationMode: keyof typeof NormalisationModesAndProperties,
) {
  return getNormalisedValue(abundance, normalisationMode) != null;
}

export function getNormalisedValue(
  abundanceOrResult: StrippedFullAbundance | Pick<AssayResult, 'value' | 'normalisationMode'>,
  normalisationMode: keyof typeof NormalisationModesAndProperties,
) {
  if ('value' in abundanceOrResult && 'normalisationMode' in abundanceOrResult) {
    if (abundanceOrResult.normalisationMode === normalisationMode) {
      return abundanceOrResult.value;
    } else {
      throw Error(`Normalisation mode mismatch: ${abundanceOrResult.normalisationMode} !== ${normalisationMode}`);
    }
  }
  const valueByMode = abundanceOrResult.valuesByMode?.[normalisationMode];
  if (valueByMode !== undefined) {
    return valueByMode;
  }
  if (
    normalisationMode === NormalisationMode.LITRE_AY ||
    normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_AY
  ) {
    return null;
  }
  const modeProperty = NormalisationModesAndProperties[normalisationMode];
  return abundanceOrResult[modeProperty];
}

export const NormalisedValueName = {
  [NormalisationMode.SIXTEEN_S]: 'Relative abundance',
  [NormalisationMode.MG_SS]: 'Copy numbers per mg of SS',
  [NormalisationMode.HOUR]: 'Copy numbers per hour',
  [NormalisationMode.LITRE_16S]: 'Copy num. per L (16S std. curve)',
  [NormalisationMode.TEN_UL_DILUTED_DNA_16S]: 'Copies per 10 uL DNA (16S std. curve)',
  [NormalisationMode.LITRE_AY]: 'Copy num. per L (ay std. curve)',
  [NormalisationMode.TEN_UL_DILUTED_DNA_AY]: 'Copies per 10 uL DNA (ay std. curve)',
  [NormalisationMode.MG_BOD]: 'Copy numbers per mg BOD',
} as const;

export function getNormalisationDescription(mode: NormalisationMode): { description: string; valueTip: string } {
  switch (mode) {
    case NormalisationMode.MG_SS:
      return {
        description: 'Copies / mg of SS (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per mg of suspended solids',
      };
    case NormalisationMode.HOUR:
      return {
        description: 'Copies per hour (log)',
        valueTip: 'The graph presents Log₁₀ of copies per hour (flow normalised)',
      };
    case NormalisationMode.LITRE_16S:
      return {
        description: 'Copy num. per L (16S std. curve) (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per litre (16S standard curve)',
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_16S:
      return {
        description: 'Copies per 10 uL DNA (16S std. curve) (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per 10 uL DNA (16S standard curve)',
      };
    case NormalisationMode.LITRE_AY:
      return {
        description: 'Copy num. per L (ay std. curve) (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per litre (assay standard curve)',
      };
    case NormalisationMode.MG_BOD:
      return {
        description: 'Copies / mg BOD (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per mg of biochemical oxygen demand',
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_AY:
      return {
        description: 'Copies per 10 uL DNA (ay std. curve) (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per 10 uL DNA (assay standard curve)',
      };
    case NormalisationMode.SIXTEEN_S:
      return {
        description: 'Relative abundance (log)',
        valueTip: 'The graph presents Log₁₀ of abundance, relative to 16S rRNA genes',
      };
  }
}

export function getLegendNormalisationDescription(mode: NormalisationMode): { description: string; valueTip: string } {
  const { valueTip } = getNormalisationDescription(mode);

  switch (mode) {
    case NormalisationMode.MG_SS:
      return {
        description: 'Copies / mg SS (log)',
        valueTip,
      };
    case NormalisationMode.HOUR:
      return {
        description: 'Copies / hour (log)',
        valueTip,
      };
    case NormalisationMode.LITRE_16S:
      return {
        description: 'Copies / L (16S, log)',
        valueTip,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_16S:
      return {
        description: 'Copies / 10 uL DNA (16S, log)',
        valueTip,
      };
    case NormalisationMode.LITRE_AY:
      return {
        description: 'Copies / L (AY, log)',
        valueTip,
      };
    case NormalisationMode.MG_BOD:
      return {
        description: 'Copies / mg BOD (log)',
        valueTip,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_AY:
      return {
        description: 'Copies / 10 uL DNA (AY, log)',
        valueTip,
      };
    case NormalisationMode.SIXTEEN_S:
      return {
        description: 'Relative abundance (log)',
        valueTip,
      };
  }
}

export function getHeatmapNormalisationDescription(mode: NormalisationMode): { description: string; valueTip: string } {
  const { valueTip } = getNormalisationDescription(mode);

  switch (mode) {
    case NormalisationMode.MG_SS:
      return {
        description: 'Copies / mg SS',
        valueTip,
      };
    case NormalisationMode.HOUR:
      return {
        description: 'Copies / hour',
        valueTip,
      };
    case NormalisationMode.LITRE_16S:
      return {
        description: 'Copies / L (16S)',
        valueTip,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_16S:
      return {
        description: 'Copies / 10 uL DNA (16S)',
        valueTip,
      };
    case NormalisationMode.LITRE_AY:
      return {
        description: 'Copies / L (AY)',
        valueTip,
      };
    case NormalisationMode.MG_BOD:
      return {
        description: 'Copies / mg BOD',
        valueTip,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_AY:
      return {
        description: 'Copies / 10 uL DNA (AY)',
        valueTip,
      };
    case NormalisationMode.SIXTEEN_S:
      return {
        description: 'Relative abundance',
        valueTip,
      };
  }
}

// Display normalization mode labels based on screen size
export function getShortNormalisationLabel(mode: NormalisationMode, isWideScreen: boolean = true): string {
  switch (mode) {
    case NormalisationMode.SIXTEEN_S:
      return isWideScreen ? 'Relative abundances' : 'Rel. abund.';
    case NormalisationMode.MG_SS:
      return isWideScreen ? 'Copy num. per mg SS' : 'Per mg SS';
    case NormalisationMode.HOUR:
      return isWideScreen ? 'Copy num. per hour' : 'Per hour';
    case NormalisationMode.LITRE_16S:
      return isWideScreen ? 'Copy num. per L (16S std. curve)' : 'Per L (16S)';
    case NormalisationMode.TEN_UL_DILUTED_DNA_16S:
      return isWideScreen ? 'Copies per 10 uL DNA (16S std. curve)' : 'Per 10 uL (16S)';
    case NormalisationMode.LITRE_AY:
      return isWideScreen ? 'Copy num. per L (ay std. curve)' : 'Per L (ay)';
    case NormalisationMode.TEN_UL_DILUTED_DNA_AY:
      return isWideScreen ? 'Copies per 10 uL DNA (ay std. curve)' : 'Per 10 uL (ay)';
    case NormalisationMode.MG_BOD:
      return isWideScreen ? 'Copy num. per mg BOD' : 'Per mg BOD';
    default:
      return 'Unknown';
  }
}
